import {
  bindService,
  createServiceToken,
  type AppFeatureDefinition,
  type ServiceBinding,
} from "@nasam/kernel";
import {
  branchesRepositoryToken,
  companiesRepositoryToken,
  type BranchesRepository,
  type CompaniesRepository,
} from "../../contracts";
import { createAssetsHttpRepository } from "./data";
import type { AssetsRepository } from "./domain";
import { createAssetTypesHttpRepository } from "./assetTypes/data";
import type { AssetTypesRepository } from "./assetTypes/domain";
import { createAssetsRoutes } from "./presentation/routes";
import { ASSET_TYPES_VIEW, ASSETS_VIEW } from "./assetsPermissions";

export * from "./assetsPermissions";
export const assetTypesRepositoryToken =
  createServiceToken<AssetTypesRepository>("asset-types.repository");
export const assetsRepositoryToken =
  createServiceToken<AssetsRepository>("assets.repository");

function createAssetsServiceBindings(
  httpClient: Parameters<
    NonNullable<AppFeatureDefinition["provideServices"]>
  >[0]["httpClient"],
): readonly ServiceBinding[] {
  return [
    bindService(
      assetTypesRepositoryToken,
      createAssetTypesHttpRepository(httpClient),
    ),
    bindService(assetsRepositoryToken, createAssetsHttpRepository(httpClient)),
  ];
}

export const assetsFeature: AppFeatureDefinition = {
  id: "assets",
  version: "0.1.0",
  titleKey: "assets:moduleTitle",
  icon: "Box",
  basePath: "/assets",
  requiredPermissions: [ASSET_TYPES_VIEW, ASSETS_VIEW],
  // Every CORE feature shares one "core" rail node (see mergeFeatureNavNodes);
  // this feature contributes the "Assets" drawer group under it.
  navigation: [
    {
      id: "core",
      titleKey: "common:businessModules.core",
      icon: "Layers",
      placement: "rail",
      order: 10,
      children: [
        {
          id: "assets",
          titleKey: "assets:nav.assetsAndDevices",
          icon: "Box",
          placement: "drawer",
          order: 40,
          requiredPermissions: [ASSET_TYPES_VIEW, ASSETS_VIEW],
          children: [
            {
              id: "assets-types",
              titleKey: "assets:nav.assetTypes",
              icon: "Boxes",
              placement: "drawer",
              order: 10,
              path: "/assets/types",
              requiredPermissions: [ASSET_TYPES_VIEW],
            },
            {
              id: "assets-list",
              titleKey: "assets:nav.assets",
              icon: "Box",
              placement: "drawer",
              order: 20,
              path: "/assets",
              requiredPermissions: [ASSETS_VIEW],
            },
          ],
        },
      ],
    },
  ],
  routes: (context) =>
    createAssetsRoutes({
      context,
      assets: context.services.resolve(assetsRepositoryToken),
      assetTypes: context.services.resolve(assetTypesRepositoryToken),
      companies: context.services.tryResolve<CompaniesRepository>(
        companiesRepositoryToken,
      ),
      branches: context.services.tryResolve<BranchesRepository>(
        branchesRepositoryToken,
      ),
    }),
  permissionCatalog: [
    {
      segment: "core",
      labelKey: "assets:permissions.core",
      children: [
        {
          segment: "asset_management",
          labelKey: "assets:permissions.assetManagement",
          children: [
            {
              segment: "asset_type_management_list",
              labelKey: "assets:permissions.assetTypes",
              actions: [
                { id: "view", labelKey: "assets:actions.view" },
                { id: "create", labelKey: "assets:actions.create" },
                { id: "update", labelKey: "assets:actions.update" },
                { id: "delete", labelKey: "assets:actions.delete" },
              ],
            },
            {
              segment: "asset_management_list",
              labelKey: "assets:permissions.assets",
              actions: [
                { id: "view", labelKey: "assets:actions.view" },
                { id: "create", labelKey: "assets:actions.create" },
                { id: "update", labelKey: "assets:actions.update" },
                { id: "delete", labelKey: "assets:actions.delete" },
              ],
            },
          ],
        },
      ],
    },
  ],
  provideServices: ({ httpClient }) => createAssetsServiceBindings(httpClient),
};
